import { useEffect, useMemo } from "react"
import { AnalyticsEvents } from "~/util/analyticsEvents"
import { useSearchParams } from "react-router-dom"
import invariant from "tiny-invariant"
import {
  InfluencerSortFieldEnum,
  RosterAccessModeEnum,
} from "~/__generated__/graphql"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { MarketplaceGate } from "~/components/MarketplaceGate"
import { RosterDatabaseTeaser } from "~/components/MarketplaceTeasers"
import { useHeaderContext } from "~/common/HeaderContext"
import {
  InstagramIcon,
  TikTokIcon,
  YouTubeIcon,
} from "~/common/SocialMediaIcons"
import { categoryName, exclusivityName } from "~/common/enums"
import { useDocumentTitle } from "~/common/useDocumentTitle"
import { useTrackScreenEvent } from "~/common/useTrackScreenEvent"
import { ViewInfluencerScreen } from "~/screens/influencers/view-influencer/ViewInfluencerScreen"
import { Card, CardContent } from "~/shadcn/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/shadcn/ui/table"
import { Avatar } from "~/ui/Avatar"
import { CopyButton } from "~/ui/CopyButton"
import { ErrorBox } from "~/ui/Error"
import { InfiniteLoadMore } from "~/ui/InfiniteLoadMore"
import { LoadingIndicatorCentered } from "~/ui/LoadingIndicator"
import { ModalDialog } from "~/ui/ModalDialog"
import { friendlyNumber } from "~/util/numbers"
import { InfluencerSortProvider } from "../components/InfluencerSortContext"
import { SearchBar } from "../components/SearchBar"
import { SortableColumn } from "../components/SortableColumn"
import { useInfluencers } from "../useInfluencers"

export const RosterDatabaseScreen = () => {
  useDocumentTitle("Roster Database")
  const [searchParams, setSearchParams] = useSearchParams()
  const agencyIdParam = searchParams.get("agency")
  const user = useCurrentUser()
  const activeInfluencerId = searchParams.get("influencerId")
  const { setHeaderText, setSubheaderText } = useHeaderContext()

  const {
    influencers,
    data,
    error,
    loading,
    setSearchValues,
    sortField,
    sortDir,
    onSort,
    onLoadNextPage,
    pageInfo,
  } = useInfluencers({
    access: { mode: RosterAccessModeEnum.MarketplaceDiscovery },
    // The server refuses the listing without Marketplace access (ZAN-921).
    skip: !user.canAccessMarketplacePages.value,
  })

  useEffect(() => {
    setHeaderText("Roster Database")
  }, [setHeaderText])

  useEffect(() => {
    setSubheaderText("")
  }, [setSubheaderText])

  const initialValues = useMemo(
    () => ({ agencyIds: agencyIdParam ? [agencyIdParam] : [] }),
    [agencyIdParam]
  )

  const locked = !user.canAccessMarketplacePages.value
  const resultsCount = data?.influencers.totalCount
  useTrackScreenEvent(
    "view_roster_database",
    useMemo(
      (): AnalyticsEvents["view_roster_database"] | null =>
        locked
          ? { locked: true }
          : resultsCount === undefined
          ? null
          : { results_count: resultsCount },
      [locked, resultsCount]
    )
  )

  // Gated by MarketplaceGate, like Dashboard and Talent Agencies: without the
  // Marketplace plan the screen shows a static teaser behind the upgrade card.
  return (
    <MarketplaceGate teaser={<RosterDatabaseTeaser />}>
      <div className="h-full pb-4 relative select-none">
        <Card className="absolute left-0 right-0 top-0 bottom-4 overflow-auto">
          <CardContent className="flex-1 p-6 pt-4 flex flex-col">
            <SearchBar
              setSearchValues={setSearchValues}
              initialValues={initialValues}
              showAgencyFilter
              source="roster_database"
            />
            {!data && loading ? (
              <LoadingIndicatorCentered />
            ) : error || !data ? (
              <ErrorBox message="Error loading influencers." />
            ) : (
              <Card className="absolute top-[70px] bottom-4 left-6 right-6 flex-grow overflow-auto">
                <Table>
                  <TableHeader>
                    <InfluencerSortProvider
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={onSort}
                    >
                      <TableRow>
                        <TableHead sticky>
                          <SortableColumn field={InfluencerSortFieldEnum.Name}>
                            Name
                          </SortableColumn>
                        </TableHead>
                        <TableHead sticky>
                          <SortableColumn
                            field={InfluencerSortFieldEnum.Category}
                          >
                            Category
                          </SortableColumn>
                        </TableHead>
                        <TableHead sticky>
                          <SortableColumn
                            field={InfluencerSortFieldEnum.Country}
                          >
                            Country
                          </SortableColumn>
                        </TableHead>
                        <TableHead sticky>
                          <SortableColumn
                            field={InfluencerSortFieldEnum.TikTokFollowers}
                          >
                            <div className="flex gap-1 items-center">
                              <TikTokIcon className="w-[18px] h-[20px]" />
                              TT
                            </div>
                          </SortableColumn>
                        </TableHead>
                        <TableHead sticky>
                          <SortableColumn
                            field={InfluencerSortFieldEnum.InstagramFollowers}
                          >
                            <div className="flex gap-1 items-center">
                              <InstagramIcon className="w-[18px] h-[18px]" />
                              IG
                            </div>
                          </SortableColumn>
                        </TableHead>
                        <TableHead sticky>
                          <SortableColumn
                            field={InfluencerSortFieldEnum.YoutubeSubscribers}
                          >
                            <div className="flex gap-1 items-center">
                              <YouTubeIcon className="w-[19px] h-[13px]" />
                              YT
                            </div>
                          </SortableColumn>
                        </TableHead>
                        <TableHead sticky>
                          <SortableColumn
                            field={InfluencerSortFieldEnum.AgencyName}
                          >
                            Agency Name
                          </SortableColumn>
                        </TableHead>
                        <TableHead sticky>
                          <SortableColumn
                            field={InfluencerSortFieldEnum.Exclusivity}
                          >
                            Exclusivity
                          </SortableColumn>
                        </TableHead>
                        <TableHead sticky>
                          <SortableColumn
                            field={InfluencerSortFieldEnum.AgentEmail}
                          >
                            Contact Email
                          </SortableColumn>
                        </TableHead>
                      </TableRow>
                    </InfluencerSortProvider>
                  </TableHeader>
                  <TableBody>
                    {influencers
                      .filter((influencer) => !influencer.deleted)
                      .map((influencer) => (
                        <TableRow
                          key={influencer.id}
                          onClick={() => {
                            invariant(
                              influencer.id,
                              "Influencer ID is required"
                            )
                            setSearchParams(
                              (params) => {
                                invariant(
                                  influencer.id,
                                  "Influencer ID is required"
                                )
                                params.set("influencerId", influencer.id)
                                return params
                              },
                              { replace: true }
                            )
                          }}
                          className="cursor-pointer"
                        >
                          <TableCell>
                            <div className="flex flex-row items-center gap-2">
                              <Avatar
                                src={influencer.profileImageThumbnailUrl}
                                alt={influencer.name}
                              />
                              {influencer.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            {categoryName(influencer.category)}
                          </TableCell>
                          <TableCell>{influencer.countryData?.name}</TableCell>
                          <TableCell>
                            {friendlyNumber(
                              influencer.tikTokProfile?.followers
                            )}
                          </TableCell>
                          <TableCell>
                            {friendlyNumber(
                              influencer.instagramProfile?.followers
                            )}
                          </TableCell>
                          <TableCell>
                            {friendlyNumber(
                              influencer.youtubeProfile?.subscribers
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>
                                {influencer.agency?.name ?? "No Agency"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {exclusivityName(influencer.exclusivity)}
                          </TableCell>
                          <TableCell>
                            {influencer.agentEmail && (
                              <CopyButton
                                textToCopy={influencer.agentEmail}
                                successMessage={`"${influencer.agentEmail}" copied to clipboard`}
                                label={influencer.agentEmail}
                                className="w-full justify-between"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <InfiniteLoadMore
                  onEndReached={onLoadNextPage}
                  canLoadMore={!loading && !!pageInfo?.hasNextPage}
                  loadingText="Loading more..."
                  loading={loading && influencers.length > 0}
                  className="p-4"
                />
                {loading && data.influencers.edges.length === 0 && (
                  <div className="p-4">
                    <LoadingIndicatorCentered />
                  </div>
                )}
              </Card>
            )}
          </CardContent>
        </Card>
        <ModalDialog
          open={!!activeInfluencerId}
          onClose={() => {
            setSearchParams(
              (params) => {
                params.delete("influencerId")
                return params
              },
              { replace: true }
            )
          }}
        >
          {activeInfluencerId && (
            <ViewInfluencerScreen
              influencerId={activeInfluencerId}
              source="roster_database"
            />
          )}
        </ModalDialog>
      </div>
    </MarketplaceGate>
  )
}
